<template>
  <div class="text-editor">
    <editor-menu-bubble
      class="menububble"
      :editor="editor"
      @hide="hideLinkMenu"
      v-slot="{ commands, isActive, getMarkAttrs, menu }"
    >
      <div
        class="menububble"
        :class="{ 'is-active': menu.isActive }"
        :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
      >
        <form
          class="menububble__form"
          v-if="linkMenuIsActive"
          @submit.prevent="setLinkUrl(commands.link, linkUrl)"
        >
          <input
            class="menububble__input"
            type="text"
            v-model="linkUrl"
            placeholder="https://"
            ref="linkInput"
            @keydown.esc="hideLinkMenu"
          >
          <button class="menububble__button" @click="setLinkUrl(commands.link, null)" type="button">
            <fai icon="trash"></fai>
          </button>
        </form>

        <template v-else>
          <button
            class="menububble__button"
            @click="showLinkMenu(getMarkAttrs('link'))"
            :class="{ 'is-active': isActive.link() }"
          >
            <fai icon="link"></fai>
          </button>
        </template>
      </div>
    </editor-menu-bubble>

    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
      <div class="menubar">
        <div
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() }"
          @click="commands.bold"
        >
          <fai icon="bold"></fai>
        </div>

        <div
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() }"
          @click="commands.italic"
        >
          <fai icon="italic"></fai>
        </div>

        <div
          class="menubar__button"
          :class="{ 'is-active': isActive.strike() }"
          @click="commands.strike"
        >
          <fai icon="strikethrough"></fai>
        </div>

        <div
          class="menubar__button"
          :class="{ 'is-active': isActive.underline() }"
          @click="commands.underline"
        >
          <fai icon="underline"></fai>
        </div>

        <div
          class="menubar__button"
          :class="{ 'is-active': isActive.code() }"
          @click="commands.code"
        >
          <fai icon="code"></fai>
        </div>

        <div
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click="commands.heading({ level: 1 })"
        >H1</div>

        <div
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click="commands.heading({ level: 2 })"
        >H2</div>

        <div
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click="commands.heading({ level: 3 })"
        >H3</div>

        <div
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click="commands.bullet_list"
        >
          <fai icon="list-ul"></fai>
        </div>

        <div
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click="commands.ordered_list"
        >
          <fai icon="list-ol"></fai>
        </div>

        <div class="menubar__button" @click="commands.horizontal_rule">
          <fai icon="grip-lines"></fai>
        </div>

        <div class="menubar__button" @click="commands.undo">
          <fai icon="undo"></fai>
        </div>

        <div class="menubar__button" @click="commands.redo">
          <fai icon="redo"></fai>
        </div>
      </div>
    </editor-menu-bar>

    <editor-content class="text-editor__content" :editor="editor"/>
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar, EditorMenuBubble } from 'tiptap'
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Link,
  Strike,
  Underline,
  History,
} from 'tiptap-extensions'
export default {
  components: {
    EditorContent,
    EditorMenuBar,
    EditorMenuBubble
  },
  data() {
    return {
      linkMenuIsActive: false,
      linkUrl: null,
      editor: new Editor({
        extensions: [
          new BulletList(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new Link(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History()
        ],
        content: '',
        onUpdate: ({ getHTML }) => {
          const state = getHTML()
          this.$emit('change', state)
        }
      }),
    }
  },
  beforeDestroy() {
    this.editor.destroy()
  },

  created() {
    for (let i = 0; i < this.editor.inputRules.length; i++) {
      // Delete rule which inserts <em> between 2 underscores and prevent buggy context variables
      if (this.editor.inputRules[i].match.toString() === '/(?:^|[^_])(_([^_]+)_)$/') this.$delete(this.editor.inputRules, i)
    }
  },

  methods: {
    showMenu() {

    },

    hideMenu() {

    },

    showLinkMenu(attrs) {
      this.linkUrl = attrs.href
      this.linkMenuIsActive = true
      this.$nextTick(() => {
        this.$refs.linkInput.focus()
      })
    },
    hideLinkMenu() {
      this.linkUrl = null
      this.linkMenuIsActive = false
    },
    setLinkUrl(command, url) {
      if (!url || !url.length) return
      command({ href: url })
      this.hideLinkMenu()
    },
  }
}
</script>

<style lang="scss">
.ProseMirror {
  outline: none;
}

.text-editor {
  &__content {
    max-height: 400px;
    overflow-y: auto;
    outline: none;
  }
}

.menubar {
  display: flex;
  justify-content: space-around;
  border-bottom: 1px solid $borderGrey;
  padding-bottom: 12px;

  &__button {
    flex-grow: 1;
    font-size: 0.9em;
    padding: 5px;
    text-align: center;
    transition: background-color 0.2s ease;
    cursor: pointer;

    &:hover {
      background-color: $bgLightGrey;
    }

    &:not(:last-of-type) {
      border-right: 1px solid $borderGrey;
    }
  }
}

.menububble {
  position: absolute;
  opacity: 0;
  background-color: #3a3a3a;
  border-radius: 7px;
  padding: 5px 5px;
  z-index: 10;

  &.is-active {
    opacity: 1;
  }

  &__form {
    display: flex;
    align-items: center;
  }

  &__button {
    display: block;
    font-size: 1em;
    border: none;
    border-radius: 7px;
    background-color: #3a3a3a;
    color: white;
    cursor: pointer;
  }

  &__input {
    display: block;
    outline: none;
    width: 170px;
    color: white;
    border: none;
    background-color: transparent;
  }
}
</style>
